// FacilityPicker — the screen that stays up rather than guess.
//
// This is the whole surface when `useDeviceFacility` returns no facility. It is
// deliberately NOT a banner over a rendered board: there is no board to put it
// over, because the one thing this feature must never do is render an OR board
// for a hospital nobody confirmed.
//
// Read the reason chip as the entire variable content — the picker itself is
// identical in every failure mode, so a wall that has shown it once is legible
// the second time without re-reading the page.
//
// ACCESSIBILITY NOTES THAT ARE DECISIONS, NOT OMISSIONS
//  • It is a real dialog: role="dialog", aria-modal, labelled by its heading,
//    and Tab is trapped inside it. There is nothing behind it to reach.
//  • There is NO Escape handler. Escape closes a modal so the user can get back
//    to what was underneath; here "underneath" is an unidentified facility.
//    Dismissing this gate is exactly the failure it exists to prevent.
//  • The reason is carried by TEXT, never by the chip's colour. The distance
//    badge says "IN RANGE" in words as well as turning green.
//  • One live region on this surface, on the reason line, so a chip that
//    changes underneath a watching human (checking → blocked) is announced.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { MONO } from "./panelCard.styles";

// Design spec §3.2/§3.3. Device chrome is dark; this screen is read from
// further away than the board and has nothing else on it, hence --u: 27px.
const U = 27;
const RAIL = "#10203f";
const RAIL_CARD = "#1b2f52";
const RAIL_INK = "#eaf1fb";       // 14.21:1 on rail
const RAIL_INK2 = "#a8bcd8";      // 8.34:1 on rail
const AMBER = "#f59e0b";          // ink on it 8.89:1
const INK = "#0f172a";
const GREEN_NAVY = "#5ee9b5";     // 10.61:1 on rail
const BLUE_NAVY = "#8ab4ff";      // 7.73:1 on rail
const EDGE = "rgba(168, 188, 216, 0.5)";

// The chip is the only thing that varies between failure modes. Every string is
// a statement of fact about what the device knows, never a euphemism.
const REASON_CHIP = {
  locating: "CHECKING LOCATION",
  ambiguous: "TWO CAMPUSES IN RANGE",
  denied: "LOCATION BLOCKED",
  unavailable: "NO LOCATION SENSOR",
  unconfigured: "NO GEOFENCE CONFIGURED",
  "out-of-range": "NOT INSIDE ANY FACILITY FENCE",
  mismatch: "LOCATION DOES NOT MATCH THIS DEVICE",
  "account-scope": "THIS ACCOUNT CANNOT OPEN THAT FACILITY",
};

const REASON_LEDE = {
  locating:
    "Asking this device where it is. Pick a facility now if you would rather not wait — the choice sticks to this device.",
  ambiguous:
    "This wall display could not tell which hospital it is in — two campuses fall inside the same fence at this position. Nothing is shown until someone picks one. The choice sticks to this device.",
  denied:
    "Location access is blocked for this site, so the display cannot confirm which hospital it is in. Pick the facility, or allow location in the browser settings and retry. The choice sticks to this device.",
  unavailable:
    "This device could not produce a position — many wall displays have no location sensor at all. Pick the facility; the choice sticks to this device.",
  unconfigured:
    "No facility on this account has campus coordinates stored, so there is nothing to match this device against. Pick the facility, and store the coordinates on the facility record to turn the fence on.",
  "out-of-range":
    "This device produced a position, but it is not inside any facility's configured fence. Pick the facility, or check the stored coordinates and radius.",
  mismatch:
    "The facility saved on this device is not the one its location resolves to. That is either a display that moved or a position that cannot be trusted, and both need a human. Confirm which hospital this display is in.",
  "account-scope":
    "This display resolved to a facility the signed-in account cannot open. Alerts and break requests are scoped to the account's own facility, so this board would look calm while emergencies went unseen. Pick a facility this account can open, or sign in as the right account.",
};

const POLICY_NOTE =
  "The panel never guesses a facility. If location is blocked, missing, or ambiguous, this screen stays up — an OR board showing the wrong hospital is worse than a board showing nothing.";

const FOCUSABLE =
  'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

function kmLabel(distanceM) {
  if (!Number.isFinite(distanceM)) return null;
  const km = distanceM / 1000;
  return `${km < 10 ? km.toFixed(1) : Math.round(km)} KM`;
}

function FocusRingButton({ style, focusStyle, children, ...rest }) {
  const [focused, setFocused] = useState(false);
  return (
    <button
      type="button"
      {...rest}
      onFocus={(e) => {
        setFocused(true);
        if (rest.onFocus) rest.onFocus(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        if (rest.onBlur) rest.onBlur(e);
      }}
      style={{
        ...style,
        ...(focused
          ? {
              outline: `${Math.round(U * 0.15)}px solid ${BLUE_NAVY}`,
              outlineOffset: 3,
              borderColor: BLUE_NAVY,
              ...focusStyle,
            }
          : null),
      }}
    >
      {children}
    </button>
  );
}

const S = {
  gate: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: RAIL,
    color: RAIL_INK,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: U * 0.7,
    padding: U * 2,
    overflowY: "auto",
    fontFamily: "inherit",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: U * 0.35,
    fontFamily: MONO,
    fontSize: U * 0.7,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: INK,
    background: AMBER,
    borderRadius: U * 0.2,
    padding: `${U * 0.3}px ${U * 0.6}px`,
  },
  title: {
    fontSize: U * 2.2,
    fontWeight: 700,
    margin: 0,
    letterSpacing: "-0.02em",
    textAlign: "center",
    lineHeight: 1.08,
  },
  lede: {
    fontSize: U * 0.95,
    lineHeight: 1.5,
    color: RAIL_INK2,
    maxWidth: "54ch",
    textAlign: "center",
    margin: 0,
  },
  list: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: U * 0.6,
    width: `min(100%, ${U * 44}px)`,
    marginTop: U * 0.3,
    listStyle: "none",
    padding: 0,
  },
  card: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    alignItems: "center",
    gap: U * 0.6,
    textAlign: "left",
    width: "100%",
    // 4u = 108 px, far above the 44 px minimum target.
    minHeight: U * 4,
    background: RAIL_CARD,
    // Longhand, not the `border` shorthand: the focus ring swaps borderColor
    // alone, and React warns (correctly) that mixing the two shapes on the same
    // element can drop the property on rerender.
    borderWidth: Math.round(U * 0.1),
    borderStyle: "solid",
    borderColor: "rgba(168, 188, 216, 0.35)",
    borderRadius: U * 0.3,
    padding: `${U * 0.7}px ${U * 0.8}px`,
    color: RAIL_INK,
    font: "inherit",
    cursor: "pointer",
  },
  cardName: { display: "block", fontSize: U * 1.05, fontWeight: 700, lineHeight: 1.15 },
  cardSub: {
    display: "block",
    fontFamily: MONO,
    fontSize: U * 0.68,
    color: RAIL_INK2,
    letterSpacing: "0.04em",
    marginTop: U * 0.15,
  },
  badge: {
    fontFamily: MONO,
    fontSize: U * 0.6,
    fontWeight: 700,
    letterSpacing: "0.1em",
    borderRadius: 999,
    padding: `${U * 0.15}px ${U * 0.45}px`,
    whiteSpace: "nowrap",
  },
  foot: { display: "flex", gap: U * 0.6, marginTop: U * 0.5, flexWrap: "wrap", justifyContent: "center" },
  footBtn: {
    fontFamily: "inherit",
    fontSize: U * 0.8,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    minHeight: U * 2.4,
    minWidth: U * 2.4,
    padding: `${U * 0.5}px ${U * 1.1}px`,
    borderRadius: U * 0.25,
    background: "transparent",
    color: RAIL_INK,
    borderWidth: 1.5,
    borderStyle: "solid",
    borderColor: EDGE,
    cursor: "pointer",
  },
  note: {
    fontFamily: MONO,
    fontSize: U * 0.66,
    color: RAIL_INK2,
    letterSpacing: "0.05em",
    textAlign: "center",
    margin: `${U * 0.4}px 0 0`,
    maxWidth: "56ch",
    lineHeight: 1.5,
  },
  empty: { fontSize: U * 0.9, color: RAIL_INK2, textAlign: "center", maxWidth: "54ch" },
};

/**
 * @param {object}   props
 * @param {string}   props.reason      one of useDeviceFacility's REASONS
 * @param {Array}    props.candidates  [{ facilityId, label, distanceM, radiusM,
 *                                        inRange, sub? }] — distanceM is null
 *                                      whenever no fix exists, and a null
 *                                      distance renders NO badge
 * @param {Function} props.onChoose    (facilityId) => void — persists the pick
 * @param {Function} props.onRetry     re-runs the geolocation lifecycle
 * @param {Function} [props.onSignOut] omitted ⇒ no sign-out button
 * @param {boolean}  [props.busy]      a fix is in flight; Retry is disabled
 */
export default function FacilityPicker({
  reason,
  candidates,
  onChoose,
  onRetry,
  onSignOut,
  busy = false,
}) {
  const gateRef = useRef(null);
  const headingId = "facility-picker-title";
  const rows = Array.isArray(candidates) ? candidates : [];
  const chip = REASON_CHIP[reason] || REASON_CHIP.unavailable;
  const lede = REASON_LEDE[reason] || REASON_LEDE.unavailable;

  // Move focus in on mount so a keyboard or remote-control user is not left
  // tabbing through a page that is no longer reachable behind the gate.
  useEffect(() => {
    const node = gateRef.current;
    if (!node) return;
    const first = node.querySelector(FOCUSABLE);
    if (first && typeof first.focus === "function") first.focus();
  }, []);

  // Trap Tab. No Escape branch on purpose — see the header.
  const onKeyDown = useCallback((e) => {
    if (e.key !== "Tab") return;
    const node = gateRef.current;
    if (!node) return;
    const items = Array.from(node.querySelectorAll(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  return (
    // A role=dialog with its own key handling; the handler IS the keyboard
    // path, and focus is moved into it on open.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={gateRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      data-testid="facility-picker"
      data-reason={reason || "unavailable"}
      onKeyDown={onKeyDown}
      style={S.gate}
    >
      {/* The chip is amber-on-ink, but the words carry the meaning: a greyscale
          photograph of this wall still says why it is up. */}
      <span role="status" aria-live="polite" data-testid="picker-reason" style={S.chip}>
        <span aria-hidden="true">◎</span>
        {chip}
      </span>

      <h1 id={headingId} style={S.title}>
        Choose this display&rsquo;s facility
      </h1>
      <p style={S.lede}>{lede}</p>

      {rows.length === 0 ? (
        <p style={S.empty} data-testid="picker-empty">
          No facilities are available to this account. Sign in with an account that has a
          facility, or add one in Setup.
        </p>
      ) : (
        <ul style={S.list}>
          {rows.map((c) => {
            const km = kmLabel(c.distanceM);
            return (
              <li key={c.facilityId}>
                <FocusRingButton
                  onClick={() => onChoose && onChoose(c.facilityId)}
                  data-testid={`pick-${c.facilityId}`}
                  style={S.card}
                >
                  <span aria-hidden="true" style={{ fontSize: U * 1.4, lineHeight: 1 }}>
                    {c.inRange ? "◉" : "○"}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span style={S.cardName}>{c.label || c.facilityId}</span>
                    {c.sub ? <span style={S.cardSub}>{c.sub}</span> : null}
                  </span>
                  {/* No fix ⇒ no badge at all. A badge reading "—" invites the
                      reader to believe a distance was measured and came back
                      empty, when nothing was ever measured. */}
                  {km ? (
                    <span
                      data-testid={`distance-${c.facilityId}`}
                      style={{
                        ...S.badge,
                        border: `1.5px solid ${c.inRange ? GREEN_NAVY : EDGE}`,
                        color: c.inRange ? GREEN_NAVY : RAIL_INK2,
                      }}
                    >
                      {c.inRange ? `IN RANGE · ${km}` : km}
                    </span>
                  ) : null}
                </FocusRingButton>
              </li>
            );
          })}
        </ul>
      )}

      <div style={S.foot}>
        <FocusRingButton
          onClick={onRetry}
          disabled={busy}
          style={{ ...S.footBtn, opacity: busy ? 0.55 : 1, cursor: busy ? "default" : "pointer" }}
        >
          {busy ? "Checking location…" : "Retry location"}
        </FocusRingButton>
        {onSignOut ? (
          <FocusRingButton onClick={onSignOut} style={S.footBtn}>
            Sign out
          </FocusRingButton>
        ) : null}
      </div>

      <p style={S.note}>{POLICY_NOTE}</p>
    </div>
  );
}
